import type { ComponentProps, ComponentType } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useAuth } from '@/features/authentication/components/auth-provider.tsx'
import {
  IconAlertTriangle,
  IconArrowUpRight,
  IconBooks,
  IconCalendarEvent,
  IconCertificate,
  IconCircleCheck,
  IconExternalLink,
  IconRefresh,
  IconUserCheck,
} from '@tabler/icons-react'
import { useDashboardSummaryQuery } from '@/features/dashboard/api/useDashboardSummaryQuery'
import { useCpdSummaryQuery } from '@/features/dashboard/api/useCpdSummaryQuery'
import { logEvent } from '@/lib/analytics'
import { isAdminUser } from '@/lib/role-guards'
import { useGetApplicationsByStatus } from '@/utils/api/membership-service'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import type { DashboardSummary } from '@/features/dashboard/api/useDashboardSummaryQuery'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'

const QUICK_LINK_ICON_MAP: Record<string, ComponentType<{ className?: string }>> = {
  'cpd-bank': IconCertificate,
  prospectus: IconBooks,
  calendar: IconCalendarEvent,
}

type BadgeVariant = ComponentProps<typeof Badge>['variant']

const STATUS_META: Record<DashboardSummary['profile']['membershipStatus'], { label: string; badgeVariant: BadgeVariant }> = {
  active: { label: 'Active member', badgeVariant: 'secondary' },
  approved: { label: 'Active member', badgeVariant: 'secondary' },
  inactive: { label: 'Inactive', badgeVariant: 'outline' },
  disciplined: { label: 'Disciplined', badgeVariant: 'destructive' },
}

function formatDateValue(date: string | undefined, formatString: string): string {
  if (!date) return 'Date unavailable'
  const parsedDate = new Date(date)
  return isNaN(parsedDate.getTime()) ? 'Invalid date' : format(parsedDate, formatString)
}

function DashboardContent() {
  const { user } = useAuth()
  const isAdmin = isAdminUser(user)
  const dashboardSummaryQuery = useDashboardSummaryQuery()
  const cpdSummaryQuery = useCpdSummaryQuery()
  const pendingApplicationsQuery = useGetApplicationsByStatus<number>(
    'PENDING',
    isAdmin ? { page: 0, size: 1 } : undefined,
    {
      query: {
        enabled: isAdmin,
        placeholderData: (previous) => previous,
        select: (response) => {
          const payload = response?.data
          if (!payload) return 0
          if (typeof (payload as { totalElements?: unknown }).totalElements === 'number') {
            return (payload as { totalElements: number }).totalElements
          }

          const maybeContent = (payload as { content?: unknown[] }).content
          return Array.isArray(maybeContent) ? maybeContent.length : 0
        },
      },
    },
  )
  const pendingApplications = pendingApplicationsQuery.data ?? 0
  const renderSkeleton = () => (
    <div className="grid gap-4 xl:grid-cols-[2fr_1fr]">
      <Card className="border-muted">
        <CardHeader className="gap-3">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="grid gap-4 sm:grid-cols-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-24 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
      <div className="flex flex-col gap-4">
        <Card className="border-muted">
          <CardHeader className="gap-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-40" />
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
        <Card className="border-muted">
          <CardHeader className="gap-3">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-40" />
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    </div>
  )

  if (dashboardSummaryQuery.isError || cpdSummaryQuery.isError) {
    const errorMessage =
      dashboardSummaryQuery.error?.message ??
      cpdSummaryQuery.error?.message ??
      'We could not load your membership overview.'

    return (
      <div
        role="alert"
        className="border-destructive/40 bg-destructive/10 text-destructive flex flex-col gap-2 rounded-md border p-4"
      >
        <p className="font-semibold">Something went wrong</p>
        <p className="text-sm">{errorMessage}</p>
      </div>
    )
  }

  if (dashboardSummaryQuery.isPending || cpdSummaryQuery.isPending) {
    return renderSkeleton()
  }

  const dashboardSummary = dashboardSummaryQuery.data
  const cpdSummary = cpdSummaryQuery.data

  if (!dashboardSummary || !cpdSummary) {
    return renderSkeleton()
  }

  const membershipCategoryLabel = dashboardSummary?.profile.membershipCategory ?? 'Not assigned'
  const rawMembershipExpiry = dashboardSummary?.profile.membershipValidUntil ?? ''
  const expiryDate = rawMembershipExpiry || 'Not available'
  const memberSince = dashboardSummary?.profile.memberSince ?? 'Not available'
  const duesPaidThrough = dashboardSummary?.duesPaidThrough ?? 'Dues status unavailable'
  const certificateEligibility = dashboardSummary.certificateEligibility

  const memberGratitudeMessage = `Thank you for being a member since ${memberSince}.`
  const duesHeadline = duesPaidThrough ? `Paid through ${duesPaidThrough}` : 'Dues status unavailable'
  const duesDetail = duesPaidThrough ? 'Your membership is in good standing.' : 'Please renew your dues.'

  const lastUpdated = format(new Date(), 'PPpp')
  const cpdProgress = cpdSummary.requiredPoints > 0
    ? (cpdSummary.totalPoints / cpdSummary.requiredPoints) * 100
    : 0
  const remainingPoints = Math.max(0, cpdSummary.requiredPoints - cpdSummary.totalPoints)
  const membershipExpired =
    !rawMembershipExpiry ||
    Number.isNaN(new Date(rawMembershipExpiry).getTime()) ||
    new Date(rawMembershipExpiry).getTime() < Date.now()
  const membershipExpiryDisplay = formatDateValue(rawMembershipExpiry, 'PPP')

  const complianceChecks = [
    {
      id: 'dues',
      label: 'Membership dues',
      met: certificateEligibility.duesRequirementMet ?? false,
      detail: duesPaidThrough,
    },
    {
      id: 'cpd',
      label: `CPD requirement (${cpdSummary.totalPoints}/${cpdSummary.requiredPoints} pts)`,
      met: certificateEligibility.cpdRequirementMet ?? false,
      detail:
        certificateEligibility.cpdRequirementMet ||
          Math.max(0, cpdSummary.requiredPoints - cpdSummary.totalPoints) === 0
          ? 'CPD points requirement met.'
          : `${Math.max(0, cpdSummary.requiredPoints - cpdSummary.totalPoints)} pts remaining to stay compliant.`,
    },
  ]

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 xl:grid-cols-[2fr_1fr]">
        <Card className="border-muted">
          <CardHeader className="gap-2">
            <CardDescription className="uppercase tracking-wide text-xs">
              Membership overview
            </CardDescription>
            <CardTitle className="flex flex-wrap items-center gap-3 text-2xl">
              <IconUserCheck className="text-primary size-7" aria-hidden />
              <span>Welcome back, {dashboardSummary.profile.memberName}</span>
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>{dashboardSummary.profile.membershipNumber}</span>
              <span className="hidden sm:inline">•</span>
              <span>{membershipCategoryLabel}</span>
              <Badge variant={STATUS_META[dashboardSummary.profile.membershipStatus]?.badgeVariant ?? 'default'} className="uppercase tracking-wide">
                {STATUS_META[dashboardSummary.profile.membershipStatus]?.label ?? 'Unknown'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-md border border-muted/60 bg-muted/20 p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Valid until
                </p>
                <p className="text-lg font-semibold leading-tight">{expiryDate}</p>
                <p className="text-xs text-muted-foreground">Renew in advance to avoid interruptions.</p>
              </div>
              <div className="rounded-md border border-muted/60 bg-background p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Member since
                </p>
                <p className="text-lg font-semibold leading-tight">{memberSince}</p>
                <p className="text-xs text-muted-foreground">{memberGratitudeMessage}</p>
              </div>
              <div className="rounded-md border border-muted/60 bg-background p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Dues status
                </p>
                <p className="text-lg font-semibold leading-tight">{duesHeadline}</p>
                <p className="text-xs text-muted-foreground">{duesDetail}</p>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              {membershipExpired && (
                <Alert
                  className={cn(
                    'border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-background text-emerald-900 shadow-sm',
                  )}
                >
                  <IconRefresh className="size-5" aria-hidden />
                  <div className="flex flex-1 flex-wrap items-center justify-between gap-3">
                    <div>
                      <AlertTitle className="text-base font-semibold text-emerald-900">
                        Membership renewal required
                      </AlertTitle>
                      <AlertDescription className="text-sm text-emerald-800/80">
                        Your membership expired {membershipExpiryDisplay ? `on ${membershipExpiryDisplay}` : 'recently'}. Renew now to restore access to members-only features.
                      </AlertDescription>
                    </div>
                    <Button
                      asChild
                      className="bg-emerald-600 text-white hover:bg-emerald-600/90"
                    >
                      <Link to="/membership/renew">Renew membership</Link>
                    </Button>
                  </div>
                </Alert>
              )}
              {isAdmin && (
                <>
                  {pendingApplicationsQuery.isPending ? (
                    <Skeleton className="h-[72px] w-full rounded-lg bg-primary/10" />
                  ) : pendingApplicationsQuery.isError ? (
                    <Alert
                      variant="destructive"
                      className={cn(
                        'border-destructive/40 bg-gradient-to-r from-destructive/15 via-destructive/10 to-background shadow-sm',
                      )}
                    >
                      <IconAlertTriangle className="size-5 text-destructive" aria-hidden />
                      <AlertTitle className="text-destructive">
                        Unable to load pending applications
                      </AlertTitle>
                      <AlertDescription className="mt-2 flex flex-wrap items-center justify-between gap-3 text-destructive/80">
                        <span>
                          {pendingApplicationsQuery.error instanceof Error
                            ? pendingApplicationsQuery.error.message
                            : 'We could not fetch the pending applications count.'}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => pendingApplicationsQuery.refetch()}
                        >
                          Try again
                        </Button>
                      </AlertDescription>
                    </Alert>
                  ) : pendingApplications > 0 ? (
                    <Alert
                      className={cn(
                        'border-primary/40 bg-gradient-to-r from-primary/15 via-secondary/20 to-primary/10 text-primary shadow-sm',
                      )}
                    >
                      <IconAlertTriangle className="size-5 text-primary" aria-hidden />
                      <AlertTitle className="text-primary">Pending approvals</AlertTitle>
                      <AlertDescription className="mt-2 flex flex-wrap items-center justify-between gap-3 text-primary/80">
                        <span>
                          {pendingApplications === 1
                            ? 'There is 1 membership application waiting for approval.'
                            : `There are ${pendingApplications} membership applications waiting for approval.`}
                        </span>
                        <Button
                          asChild
                          size="sm"
                          className="border-primary/60 bg-primary text-primary-foreground hover:bg-primary/90"
                        >
                          <Link to="/admin/applications">Review applications</Link>
                        </Button>
                      </AlertDescription>
                    </Alert>
                  ) : null}
                </>
              )}
              <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Quick links
              </p>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {dashboardSummary.quickLinks.map(link => {
                  const Icon = QUICK_LINK_ICON_MAP[link.id] ?? IconArrowUpRight
                  const isExternal = link.href.startsWith('http')
                  const content = (
                    <div className="group flex h-full flex-col justify-between rounded-md border border-muted/60 bg-background p-4 transition-colors group-hover:border-primary/50">
                      <div className="flex items-center gap-2 text-sm font-semibold">
                        <Icon className="text-primary size-4" aria-hidden />
                        <span>{link.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-snug">{link.description}</p>
                      <span className="text-xs font-medium text-primary flex items-center gap-1">
                        Open
                        <IconArrowUpRight className="size-3 transition-transform group-hover:translate-x-1" aria-hidden />
                      </span>
                    </div>
                  )

                  if (isExternal) {
                    return (
                      <a
                        key={link.id}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => logEvent('quick_link_outbound', { id: link.id, href: link.href })}
                        className="block h-full rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        {content}
                      </a>
                    )
                  }

                  return (
                    <Link
                      key={link.id}
                      to={link.href}
                      className="block h-full rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      onClick={() => logEvent('quick_link_internal', { id: link.id, href: link.href })}
                    >
                      {content}
                    </Link>
                  )
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-4">
          <Card className="border-muted">
            <CardHeader className="gap-2">
              <CardDescription className="uppercase tracking-wide text-xs">
                Compliance shortcuts
              </CardDescription>
              <CardTitle className="flex items-center gap-2 text-xl">
                <IconCertificate className="text-primary size-6" aria-hidden />
                Certificate of Practice
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Request compliance letters once you meet dues and CPD requirements.
              </p>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-3">
                {complianceChecks.map(check => (
                  <div
                    key={check.id}
                    className="border-muted/60 bg-muted/10 flex items-start gap-3 rounded-md border p-3"
                  >
                    {check.met ? (
                      <IconCircleCheck className="text-emerald-500 size-5" aria-hidden />
                    ) : (
                      <IconAlertTriangle className="text-amber-500 size-5" aria-hidden />
                    )}
                    <div>
                      <p className="text-sm font-medium">{check.label}</p>
                      <p className="text-xs text-muted-foreground">{check.detail}</p>
                    </div>
                  </div>
                ))}
              </div>

              {certificateEligibility.eligible ? (
                <Button
                  asChild
                  onClick={() =>
                    logEvent('certificate_request_cta', {
                      origin: 'dashboard',
                      href: certificateEligibility.ctaHref,
                    })
                  }
                >
                  <Link to={certificateEligibility.ctaHref}>
                    Request certificate of practice
                    <IconExternalLink className="size-4" aria-hidden />
                  </Link>
                </Button>
              ) : (
                <div className="border-muted/60 bg-muted/20 flex items-start gap-3 rounded-md border border-dashed p-3 text-sm">
                  <IconAlertTriangle className="text-amber-500 size-5" aria-hidden />
                  <div>
                    <p className="font-medium">You&apos;re almost there</p>
                    <p className="text-xs text-muted-foreground leading-snug">
                      {certificateEligibility.message}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-muted">
            <CardHeader className="gap-2">
              <CardDescription className="uppercase tracking-wide text-xs">
                Recent CPDs
              </CardDescription>
              <CardTitle className="flex items-center gap-2 text-xl">
                <IconCertificate className="text-primary size-6" aria-hidden />
                CPD overview
              </CardTitle>
              <p className="text-sm text-muted-foreground">Last updated {lastUpdated}</p>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between text-sm font-medium">
                  <span>Progress {cpdSummary.year}</span>
                  <span>
                    {cpdSummary.totalPoints} / {cpdSummary.requiredPoints} pts
                  </span>
                </div>
                <Progress value={cpdProgress} />
                <p className="text-xs text-muted-foreground">
                  {remainingPoints > 0
                    ? `Earn ${remainingPoints} more point${remainingPoints === 1 ? '' : 's'} to reach your target.`
                    : 'You have met the CPD requirement for this year.'}
                </p>
              </div>

              <div className="flex gap-3 text-sm">
                <div className="rounded-md border border-muted/60 bg-muted/20 px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Events attended</p>
                  <p className="text-base font-semibold leading-tight">{cpdSummary.eventsCompleted}</p>
                </div>
                <div className="rounded-md border border-muted/60 bg-background px-3 py-2">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Points earned</p>
                  <p className="text-base font-semibold leading-tight">{cpdSummary.totalPoints}</p>
                </div>
              </div>

              <div className="space-y-2">
                {cpdSummary.recentEvents.map(event => (
                  <div
                    key={event.id}
                    className="border-muted/60 bg-muted/10 flex flex-col gap-1 rounded-md border p-3"
                  >
                    <p className="font-medium text-sm">{event.title}</p>
                    <p className="flex items-center gap-2 text-xs text-muted-foreground">
                      <IconCalendarEvent className="size-3.5" aria-hidden />
                      {formatDateValue(event.completedOn ?? undefined, 'PPPP') ?? 'Completion date unavailable'}
                    </p>
                    <div className="flex items-center justify-between text-xs">
                      <span>{event.pointsEarned} pts earned</span>
                      <span className="text-muted-foreground">
                        {event.certificateAvailable ? 'Certificate ready' : 'Processing certificate'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <Button
                variant="secondary"
                asChild
                onClick={() => logEvent('navigate_cpd_bank', { origin: 'dashboard' })}
              >
                <Link to="/benefits/cpd-bank">
                  View CPD Bank
                  <IconExternalLink className="size-4" aria-hidden />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardContent,
})
